/**
 * CLE-2811 — one row per run document: where it is (waiting, downloading, reading page n of N, read,
 * reused), with its own bar and a badge when scanned pages had to be read by OCR — or, batch two, when its
 * text was extracted when the file was uploaded ("Extracted at upload"), which reads as read.
 */

import { ProgressBar } from "primereact/progressbar";
import { Tag } from "primereact/tag";
import React from "react";
import styled from "styled-components";

import {
  DOCUMENTS_HEADING,
  DOCUMENT_DOWNLOADING,
  DOCUMENT_EXTRACTED_AT_UPLOAD,
  DOCUMENT_READ,
  DOCUMENT_READING,
  DOCUMENT_REUSED,
  DOCUMENT_SCANNED_OCR,
  DOCUMENT_WAITING,
  USING_UPLOAD_TEXT,
} from "../copy";
import {
  IDocumentRow,
  documentFraction,
  isDocumentRead,
} from "./progressModel";
import { GREEN, SectionTitle, thinBar } from "./styles";

const Rows = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const Row = styled.li`
  display: grid;
  grid-template-columns: 1.1rem minmax(0, 1fr);
  column-gap: 0.55rem;
  row-gap: 0.3rem;
  align-items: center;
`;

const Name = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  font-size: 0.875rem;
`;

const FileName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Caption = styled.span`
  margin-left: auto;
  flex: none;
  font-size: 0.75rem;
  color: var(--text-color-secondary);
  font-variant-numeric: tabular-nums;
`;

const pageWord = (n: number) => (n === 1 ? "page" : "pages");

const captionOf = (row: IDocumentRow): string => {
  switch (row.status) {
    case "downloading":
      return DOCUMENT_DOWNLOADING;
    case "extracting":
      return row.pages
        ? `Page ${Math.min(row.done, row.pages)} of ${row.pages}`
        : DOCUMENT_READING;
    case "done":
    case "preextracted":
      return row.pages ? `${row.pages} ${pageWord(row.pages)}` : DOCUMENT_READ;
    case "reused":
      return DOCUMENT_REUSED;
    default:
      return DOCUMENT_WAITING;
  }
};

/** What the row says to a screen reader: the badge's meaning, when there is one, then where it is. */
const labelOf = (row: IDocumentRow): string =>
  row.status === "preextracted"
    ? `${row.fileName}: ${USING_UPLOAD_TEXT}`
    : `${row.fileName}: ${captionOf(row)}`;

const iconOf = (row: IDocumentRow): { className: string; color?: string } => {
  switch (row.status) {
    case "done":
    case "preextracted":
      return { className: "pi pi-check-circle", color: GREEN };
    case "reused":
      return { className: "pi pi-replay", color: GREEN };
    case "downloading":
      return { className: "pi pi-download", color: "var(--primary-color)" };
    case "extracting":
      return { className: "pi pi-file", color: "var(--primary-color)" };
    default:
      return { className: "pi pi-file", color: "var(--text-color-secondary)" };
  }
};

function DocumentRow({ row }: { row: IDocumentRow }) {
  const fraction = documentFraction(row);
  const finished = isDocumentRead(row.status);
  const icon = iconOf(row);
  return (
    <Row>
      <i
        className={icon.className}
        style={{ color: icon.color, fontSize: "0.95rem" }}
      />
      <Name>
        <FileName title={row.fileName}>{row.fileName}</FileName>
        {row.status === "preextracted" ? (
          <Tag
            value={DOCUMENT_EXTRACTED_AT_UPLOAD}
            severity="success"
            rounded
            style={{ fontSize: "0.68rem", padding: "0.1rem 0.45rem" }}
            title={USING_UPLOAD_TEXT}
          />
        ) : null}
        {row.ocrPages > 0 ? (
          <Tag
            value={DOCUMENT_SCANNED_OCR}
            severity="info"
            rounded
            style={{ fontSize: "0.68rem", padding: "0.1rem 0.45rem" }}
            title={`${row.ocrPages} scanned ${pageWord(
              row.ocrPages
            )} read by OCR`}
          />
        ) : null}
        <Caption>{captionOf(row)}</Caption>
      </Name>
      <span />
      <ProgressBar
        mode={fraction == null ? "indeterminate" : "determinate"}
        value={fraction == null ? undefined : Math.round(fraction * 100)}
        showValue={false}
        color={finished ? GREEN : undefined}
        style={{
          ...thinBar(0.3),
          opacity: row.status === "pending" ? 0.6 : 1,
        }}
        aria-label={labelOf(row)}
      />
    </Row>
  );
}

interface IDocumentListProps {
  rows: IDocumentRow[];
}

function DocumentList({ rows }: IDocumentListProps) {
  if (rows.length === 0) return null;
  const read = rows.filter((r) => isDocumentRead(r.status)).length;
  return (
    <section>
      <SectionTitle>
        {DOCUMENTS_HEADING}
        <span className="font-normal text-color-secondary">
          · {read} of {rows.length} read
        </span>
      </SectionTitle>
      <Rows>
        {rows.map((row) => (
          <DocumentRow key={row.id} row={row} />
        ))}
      </Rows>
    </section>
  );
}

export default DocumentList;
